import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import { EFormBuilderFieldType, TFormBuilderDateField } from '@/types';

type TProps = {
  field: TFormBuilderDateField;
  index: number;
};

export const FormBuilderDate =
  /**
   * Date field component for form builder with editable label
   * @param {TProps} props - Component props
   * @param {TFormBuilderDateField} props.field - Date field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder date field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();

    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeDateFields = useCallback(
      (updated: Partial<TFormBuilderDateField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );

    const error = useFormBuilderFieldWithError<TFormBuilderDateField>(index);

    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.DATE}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
        error={!!error}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              onLabelChange={(label) => changeDateFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              error={!!error?.label}
              required={field.required}
            />
          }
          required={field.required}
          onRequiredChange={(required) => changeFields({ required })}
        >
          <FormBuilderElement.Date sx={{ maxWidth: '320px' }} disabled />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderDate.displayName = 'FormBuilderDate';
